import type { ReactNode } from 'react';

import { STROKE, LAYOUT, SURFACE, TEXT } from '../theme/tokens';

// The right-hand context column the mockups put on every detail page, and the
// card primitive that fills it.
//
// ADR-031 §1 said detail hierarchies are "tabs within the content column, never
// a second column" — a rule aimed at the second full-height NAVIGATION rail that
// had grown three times. This is not that: it does not navigate, it does not
// persist across routes, and it holds the facts and actions FOR THE RECORD ON
// SCREEN. FD-ADR-043 records the distinction rather than letting the port look
// like a quiet reversal.
//
// It STACKS below the content under 1280px rather than scrolling horizontally,
// because at 1024px (the ADR-015 breakpoint the console still honours) a 320px
// column leaves the table unreadable.

export function ContextColumn({ children }: { children: ReactNode }) {
  return (
    <aside
      className="flex w-full flex-col gap-4 xl:w-auto"
      style={{ maxWidth: '100%', width: '100%' }}
    >
      {children}
    </aside>
  );
}

export interface ContextCardProps {
  title: string;
  /** Rendered at the right of the header — an Edit link, a count, a chip. */
  action?: ReactNode;
  children: ReactNode;
}

export function ContextCard({ title, action, children }: ContextCardProps) {
  return (
    <section
      className="rounded-lg border"
      style={{ borderColor: STROKE.base, backgroundColor: SURFACE.base }}
    >
      <header
        className="flex items-center justify-between gap-3 border-b px-4 py-3"
        style={{ borderColor: STROKE.subtle }}
      >
        <h2 className="m-0 text-[13px] font-semibold" style={{ color: TEXT.primary }}>
          {title}
        </h2>
        {action}
      </header>
      <div className="px-4 py-3">{children}</div>
    </section>
  );
}

/** The two-column grid a detail page uses: content, then the context column. */
export function DetailLayout({
  children,
  context,
}: {
  children: ReactNode;
  context?: ReactNode;
}) {
  if (!context) return <>{children}</>;
  return (
    <div
      className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_var(--ab-ctx)]"
      style={{ ['--ab-ctx' as string]: `${LAYOUT.contextColumnWidth}px` }}
    >
      <div className="min-w-0">{children}</div>
      <ContextColumn>{context}</ContextColumn>
    </div>
  );
}
